// src/pages/properties/PropertiesList.tsx
import React, { useEffect, useState, useRef } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/navbar/navbar';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import PriceDisplay from '../../components/PriceDisplay';
import PropertyImageCarousel from '../../components/PropertyImageCarousel';
import CurrencyToggle from '../../components/CurrencyToggle';
import ExchangeRateDisclosure from '../../components/ExchangeRateDisclosure';
import { useUser } from '../../context/UserContext';

export default function PropertiesList() {
  const { t } = useTranslation();
  const { user } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const [propiedades, setPropiedades] = useState<any[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProperties, setTotalProperties] = useState(0);
  const propertiesPerPage = 12;

  // Estados para filtros
  const [propertyType, setPropertyType] = useState(searchParams.get('propertyType') || '');
  const [searchText, setSearchText] = useState(searchParams.get('search') || '');
  const [priceRange, setPriceRange] = useState(searchParams.get('priceRange') || '');
  const [priceMin, setPriceMin] = useState(searchParams.get('priceMin') || '');
  const [priceMax, setPriceMax] = useState(searchParams.get('priceMax') || '');
  const [zonaFilter, setZonaFilter] = useState(searchParams.get('zona') || '');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [recamaras, setRecamaras] = useState(searchParams.get('recamaras') || '');
  const [banos, setBanos] = useState(searchParams.get('banos') || '');
  const [m2Min, setM2Min] = useState(searchParams.get('m2Min') || '');
  const [m2Max, setM2Max] = useState(searchParams.get('m2Max') || '');

  // Datos dinámicos de la BD
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  const [availableZones, setAvailableZones] = useState<string[]>([]);

  // Ref para scroll
  const resultsRef = useRef<HTMLDivElement>(null);

  // Cargar tipos y zonas disponibles de la BD
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const snapshot = await getDocs(query(collection(db, 'propiedades'), where('estado', '==', 'A')));
        const types = new Set<string>();
        const zones = new Set<string>();

        snapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.tipo_propiedad) types.add(data.tipo_propiedad);
          if (data.zona) zones.add(data.zona);
          if (data.direccion?.area) zones.add(data.direccion.area);
          if (data.direccion?.zona) zones.add(data.direccion.zona);
        });

        setAvailableTypes(Array.from(types).sort());
        setAvailableZones(Array.from(zones).filter(z => z).sort());
      } catch (error) {
        console.error('Error cargando opciones de filtro:', error);
      }
    };
    loadFilterOptions();
  }, []);

  // Cargar propiedades con filtros
  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        let q = query(collection(db, 'propiedades'), where('estado', '==', 'A'));
        const snapshot = await getDocs(q);
        let data = snapshot.docs.map(doc => ({ docId: doc.id, ...doc.data() } as any));

        // Filtro por tipo de propiedad
        if (propertyType) {
          data = data.filter(p =>
            p.tipo_propiedad?.toLowerCase() === propertyType.toLowerCase()
          );
        }

        // Filtro por búsqueda de texto (ubicación)
        if (searchText) {
          const searchLower = searchText.toLowerCase();
          data = data.filter(p => {
            const nombre = (p.nombre || p.titulo || '').toLowerCase();
            const ciudad = (p.ciudad || '').toLowerCase();
            const zonaRoot = (p.zona || '').toLowerCase();
            const areaRoot = (p.area || '').toLowerCase();
            const comunidad = (p.comunidad || '').toLowerCase();
            const area = (p.direccion?.area || '').toLowerCase();
            const zona = (p.direccion?.zona || '').toLowerCase();
            const calle = (p.direccion?.calle || '').toLowerCase();

            return nombre.includes(searchLower) ||
                   ciudad.includes(searchLower) ||
                   zonaRoot.includes(searchLower) ||
                   areaRoot.includes(searchLower) ||
                   comunidad.includes(searchLower) ||
                   area.includes(searchLower) ||
                   zona.includes(searchLower) ||
                   calle.includes(searchLower);
          });
        }

        // Filtro por rango de precio predefinido
        if (priceRange) {
          const [min, max] = priceRange.split('-').map(Number);
          data = data.filter(p => {
            const price = p.precio_actual_usd || 0;
            if (max === 0) return price >= min; // "1000000-0" significa "más de 1M"
            return price >= min && price <= max;
          });
        }

        // Filtro por precio min/max (del Hero)
        if (priceMin) {
          const min = parseFloat(priceMin);
          data = data.filter(p => (p.precio_actual_usd || 0) >= min);
        }
        if (priceMax) {
          const max = parseFloat(priceMax);
          data = data.filter(p => (p.precio_actual_usd || 0) <= max);
        }

        // Filtro por zona
        if (zonaFilter) {
          const zonaLower = zonaFilter.toLowerCase();
          data = data.filter(p =>
            (p.zona || '').toLowerCase().includes(zonaLower)
          );
        }

        // Filtros avanzados
        if (recamaras) {
          const numRecamaras = parseInt(recamaras);
          data = data.filter(p => (p.recamaras || 0) >= numRecamaras);
        }

        if (banos) {
          const numBanos = parseInt(banos);
          data = data.filter(p => {
            const totalBanos = (p.banos_completos || 0) + (p.medios_banos || 0) * 0.5;
            return totalBanos >= numBanos;
          });
        }

        if (m2Min) {
          const min = parseInt(m2Min);
          data = data.filter(p => (p.m2_total || p.m2_lote || 0) >= min);
        }

        if (m2Max) {
          const max = parseInt(m2Max);
          data = data.filter(p => (p.m2_total || p.m2_lote || 0) <= max);
        }

        setTotalProperties(data.length);

        // Paginación
        const startIndex = (currentPage - 1) * propertiesPerPage;
        const paginatedData = data.slice(startIndex, startIndex + propertiesPerPage);

        setPropiedades(data);
        setFilteredProperties(paginatedData);
      } catch (error) {
        console.error('Error al cargar propiedades:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [currentPage, propertyType, searchText, priceRange, priceMin, priceMax, zonaFilter, recamaras, banos, m2Min, m2Max]);

  const handleSearch = () => {
    setCurrentPage(1);
    // Actualizar URL con filtros
    const params = new URLSearchParams();
    if (propertyType) params.set('propertyType', propertyType);
    if (searchText) params.set('search', searchText);
    if (priceRange) params.set('priceRange', priceRange);
    if (priceMin) params.set('priceMin', priceMin);
    if (priceMax) params.set('priceMax', priceMax);
    if (zonaFilter) params.set('zona', zonaFilter);
    if (recamaras) params.set('recamaras', recamaras);
    if (banos) params.set('banos', banos);
    navigate(`?${params.toString()}`, { replace: true });

    // Scroll a resultados
    resultsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const clearFilters = () => {
    setPropertyType('');
    setSearchText('');
    setPriceRange('');
    setRecamaras('');
    setBanos('');
    setM2Min('');
    setM2Max('');
    setCurrentPage(1);
    navigate('', { replace: true });
  };

  const hasActiveFilters = propertyType || searchText || priceRange || recamaras || banos || m2Min || m2Max;

  return (
    <>
      {user ? (
        <UserNav
          nombreUsuario={user.nombre}
          nombreInmobiliaria={user.inmobiliaria_nombre}
        />
      ) : (
        <Navbar transparent={false} />
      )}

      {/* Hero Section con Filtros */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="text-center mb-4">
            <h1 className="fw-bold" style={{ color: '#0f322b' }}>
              {t('property.findYourIdealProperty') || 'Encuentra tu propiedad ideal'}
            </h1>
            <p className="text-muted">
              {totalProperties} {t('property.propertiesAvailable') || 'propiedades disponibles'} en Riviera Nayarit
            </p>
          </div>

          {/* Barra de filtros principal */}
          <div className="bg-white rounded-4 shadow p-3 p-md-4">
            <div className="row g-3 align-items-end">
              {/* Tipo de propiedad */}
              <div className="col-6 col-md-2">
                <label className="form-label small text-muted mb-1">Tipo</label>
                <select
                  className="form-select border-0 bg-light"
                  value={propertyType}
                  onChange={(e) => setPropertyType(e.target.value)}
                >
                  <option value="">Todos</option>
                  {availableTypes.map(type => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Ubicación */}
              <div className="col-12 col-md-4">
                <label className="form-label small text-muted mb-1">Ubicación</label>
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control border-0 bg-light ps-4"
                    placeholder="Ciudad, zona o colonia..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    list="zones-list"
                  />
                  <i className="fa fa-map-marker-alt position-absolute text-muted"
                     style={{ left: '12px', top: '50%', transform: 'translateY(-50%)' }}></i>
                  <datalist id="zones-list">
                    {availableZones.map(zone => (
                      <option key={zone} value={zone} />
                    ))}
                  </datalist>
                </div>
              </div>

              {/* Precio */}
              <div className="col-6 col-md-2">
                <label className="form-label small text-muted mb-1">Precio USD</label>
                <select
                  className="form-select border-0 bg-light"
                  value={priceRange}
                  onChange={(e) => setPriceRange(e.target.value)}
                >
                  <option value="">Cualquier precio</option>
                  <option value="0-100000">Hasta $100K</option>
                  <option value="100000-250000">$100K - $250K</option>
                  <option value="250000-500000">$250K - $500K</option>
                  <option value="500000-1000000">$500K - $1M</option>
                  <option value="1000000-0">Más de $1M</option>
                </select>
              </div>

              {/* Filtros avanzados toggle */}
              <div className="col-6 col-md-2">
                <button
                  className={`btn w-100 ${showAdvancedFilters ? 'btn-outline-primary' : 'btn-light'}`}
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                >
                  <i className="fa fa-sliders-h me-2"></i>
                  Filtros
                </button>
              </div>

              {/* Botón buscar */}
              <div className="col-12 col-md-2">
                <button
                  className="btn btn-success w-100 fw-medium"
                  onClick={handleSearch}
                  style={{ backgroundColor: '#29866a', borderColor: '#29866a' }}
                >
                  <i className="fa fa-search me-2"></i>
                  Buscar
                </button>
              </div>
            </div>

            {/* Filtros avanzados expandibles */}
            {showAdvancedFilters && (
              <div className="row g-3 mt-3 pt-3 border-top">
                <div className="col-6 col-md-2">
                  <label className="form-label small text-muted mb-1">Recámaras</label>
                  <select
                    className="form-select form-select-sm border-0 bg-light"
                    value={recamaras}
                    onChange={(e) => setRecamaras(e.target.value)}
                  >
                    <option value="">Cualquiera</option>
                    <option value="1">1+</option>
                    <option value="2">2+</option>
                    <option value="3">3+</option>
                    <option value="4">4+</option>
                    <option value="5">5+</option>
                  </select>
                </div>

                <div className="col-6 col-md-2">
                  <label className="form-label small text-muted mb-1">Baños</label>
                  <select
                    className="form-select form-select-sm border-0 bg-light"
                    value={banos}
                    onChange={(e) => setBanos(e.target.value)}
                  >
                    <option value="">Cualquiera</option>
                    <option value="1">1+</option>
                    <option value="2">2+</option>
                    <option value="3">3+</option>
                    <option value="4">4+</option>
                  </select>
                </div>

                <div className="col-6 col-md-2">
                  <label className="form-label small text-muted mb-1">M² mínimo</label>
                  <input
                    type="number"
                    className="form-control form-control-sm border-0 bg-light"
                    placeholder="Desde"
                    value={m2Min}
                    onChange={(e) => setM2Min(e.target.value)}
                  />
                </div>

                <div className="col-6 col-md-2">
                  <label className="form-label small text-muted mb-1">M² máximo</label>
                  <input
                    type="number"
                    className="form-control form-control-sm border-0 bg-light"
                    placeholder="Hasta"
                    value={m2Max}
                    onChange={(e) => setM2Max(e.target.value)}
                  />
                </div>

                <div className="col-12 col-md-4 d-flex align-items-end">
                  {hasActiveFilters && (
                    <button
                      className="btn btn-link text-danger btn-sm"
                      onClick={clearFilters}
                    >
                      <i className="fa fa-times me-1"></i>
                      Limpiar filtros
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Resultados */}
      <section className="py-5" ref={resultsRef}>
        <div className="container-fluid px-4 px-lg-5">
          {/* Header de resultados */}
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
            <div>
              <h5 className="mb-1">
                {loading ? 'Buscando...' : `${totalProperties} propiedades encontradas`}
              </h5>
              {hasActiveFilters && (
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {propertyType && (
                    <span className="badge bg-light text-dark border">
                      {propertyType} <button className="btn-close btn-close-sm ms-1" onClick={() => setPropertyType('')}></button>
                    </span>
                  )}
                  {searchText && (
                    <span className="badge bg-light text-dark border">
                      {searchText} <button className="btn-close btn-close-sm ms-1" onClick={() => setSearchText('')}></button>
                    </span>
                  )}
                  {priceRange && (
                    <span className="badge bg-light text-dark border">
                      {priceRange.replace('-', ' - $').replace('0', '∞')} <button className="btn-close btn-close-sm ms-1" onClick={() => setPriceRange('')}></button>
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="d-flex align-items-center gap-3">
              <CurrencyToggle />
              <ExchangeRateDisclosure variant="tooltip" />
            </div>
          </div>

          {/* Grid de propiedades - Pantalla completa */}
          {loading ? (
            <div className="row g-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="col-xl-3 col-lg-4 col-md-6">
                  <div className="card h-100 placeholder-glow">
                    <div className="placeholder bg-secondary" style={{ height: '200px' }}></div>
                    <div className="card-body">
                      <h5 className="placeholder col-8"></h5>
                      <p className="placeholder col-6"></p>
                      <p className="placeholder col-4"></p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredProperties.length > 0 ? (
            <>
              <div className="row g-4">
                {filteredProperties.map((p, index) => (
                  <div className="col-xl-3 col-lg-4 col-md-6" key={`${p.docId}-${index}`}>
                    <div className="card h-100 border-0 shadow-sm rounded-3 overflow-hidden property-card-hover">
                      {/* Imagen con carousel */}
                      <div className="position-relative">
                        <PropertyImageCarousel
                          images={p.fotos || []}
                          title={p.titulo || 'Propiedad'}
                          maxImages={5}
                          height="200px"
                        />

                        {/* Badges */}
                        <div className="position-absolute top-0 start-0 m-2 d-flex flex-column gap-1" style={{ zIndex: 20 }}>
                          <span className="badge bg-primary shadow-sm">
                            {p.tipo_propiedad || 'Propiedad'}
                          </span>
                          {p.destacado && (
                            <span className="badge bg-warning text-dark shadow-sm">
                              <i className="fa fa-star me-1"></i>Destacada
                            </span>
                          )}
                        </div>

                        <span className={`badge position-absolute top-0 end-0 m-2 shadow-sm ${
                          p.estado === 'A' ? 'bg-success' : 'bg-secondary'
                        }`} style={{ zIndex: 20 }}>
                          {p.estado === 'A' ? 'Disponible' : p.estado}
                        </span>
                      </div>

                      <div className="card-body d-flex flex-column">
                        {/* Precio */}
                        <div className="mb-2">
                          {(p.precio_actual_mxn > 0 || p.precio_actual_usd > 0) ? (
                            <PriceDisplay
                              priceMXN={p.precio_actual_mxn}
                              priceUSD={p.precio_actual_usd}
                              className="h5 text-success mb-0 fw-bold"
                              showExchangeRate={false}
                            />
                          ) : (
                            <span className="text-muted">Consultar precio</span>
                          )}
                        </div>

                        {/* Título */}
                        <h6 className="card-title mb-2 text-truncate" title={p.titulo}>
                          {p.titulo || p.list_number || 'Propiedad'}
                        </h6>

                        {/* Ubicación */}
                        <p className="text-muted small mb-3">
                          <i className="fa fa-map-marker-alt me-1 text-primary"></i>
                          {p.zona || p.area || p.direccion?.area || p.direccion?.zona || 'Riviera Nayarit'}
                        </p>

                        {/* Características */}
                        <div className="d-flex justify-content-between text-center border-top border-bottom py-2 mb-3">
                          <div>
                            <i className="fa fa-bed text-muted"></i>
                            <div className="small fw-medium">{p.recamaras || 0}</div>
                          </div>
                          <div>
                            <i className="fa fa-bath text-muted"></i>
                            <div className="small fw-medium">{p.banos_completos || 0}</div>
                          </div>
                          <div>
                            <i className="fa fa-ruler-combined text-muted"></i>
                            <div className="small fw-medium">{p.m2_total || p.m2_lote || '-'} m²</div>
                          </div>
                          <div>
                            <i className="fa fa-car text-muted"></i>
                            <div className="small fw-medium">{p.estacionamiento || 0}</div>
                          </div>
                        </div>

                        {/* Agencia */}
                        {p.agencia_info?.agencia_nombre && (
                          <p className="text-muted small mb-2">
                            <i className="fa fa-building me-1"></i>
                            {p.agencia_info.agencia_nombre}
                          </p>
                        )}

                        {/* Botones */}
                        <div className="mt-auto d-grid gap-2">
                          <Link
                            to={`/single-property-1/${p.docId}`}
                            className="btn btn-outline-primary btn-sm"
                          >
                            Ver detalles
                          </Link>
                          {p.agencia_info?.agencia_telefono && (
                            <a
                              href={`https://wa.me/${p.agencia_info.agencia_telefono.replace(/[\s\-\(\)]/g, '')}?text=${encodeURIComponent(`Hola, me interesa la propiedad ${p.titulo || p.list_number}`)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-success btn-sm"
                            >
                              <i className="fab fa-whatsapp me-1"></i>
                              Contactar
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Paginación */}
              {totalProperties > propertiesPerPage && (
                <div className="mt-5">
                  <nav aria-label="Paginación">
                    <ul className="pagination justify-content-center">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => {
                            setCurrentPage(currentPage - 1);
                            resultsRef.current?.scrollIntoView({ behavior: 'smooth' });
                          }}
                          disabled={currentPage === 1}
                        >
                          <i className="fa fa-chevron-left"></i>
                        </button>
                      </li>

                      {Array.from({ length: Math.min(5, Math.ceil(totalProperties / propertiesPerPage)) }, (_, i) => {
                        const pageNum = Math.max(1, currentPage - 2) + i;
                        const maxPages = Math.ceil(totalProperties / propertiesPerPage);
                        if (pageNum > maxPages) return null;
                        return (
                          <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => {
                                setCurrentPage(pageNum);
                                resultsRef.current?.scrollIntoView({ behavior: 'smooth' });
                              }}
                            >
                              {pageNum}
                            </button>
                          </li>
                        );
                      })}

                      <li className={`page-item ${currentPage >= Math.ceil(totalProperties / propertiesPerPage) ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => {
                            setCurrentPage(currentPage + 1);
                            resultsRef.current?.scrollIntoView({ behavior: 'smooth' });
                          }}
                        >
                          <i className="fa fa-chevron-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>

                  <p className="text-center text-muted small mt-2">
                    Página {currentPage} de {Math.ceil(totalProperties / propertiesPerPage)}
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-5">
              <i className="fa fa-home text-muted mb-3" style={{ fontSize: '64px' }}></i>
              <h4 className="text-muted">No se encontraron propiedades</h4>
              <p className="text-muted">Intenta ajustar los filtros de búsqueda</p>
              <button className="btn btn-primary mt-3" onClick={clearFilters}>
                <i className="fa fa-refresh me-2"></i>
                Limpiar filtros
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Estilos adicionales */}
      <style>{`
        .property-card-hover {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .property-card-hover:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0,0,0,0.12) !important;
        }
        .btn-close-sm {
          width: 0.5rem;
          height: 0.5rem;
          padding: 0.15rem;
          background-size: 0.5rem;
        }
      `}</style>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
